import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ordersService, { StorageClearedRecord } from '../../../services/orders.service';
import { useToast } from '../../../contexts/ToastContext';
import DateTimePicker from '../../../components/DateTimePicker';

/** Local-time YYYY-MM-DD (avoids the UTC drift of Date.toISOString). */
const toIsoDate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

interface StorageClearedPopoverProps {
  isOpen: boolean;
  pronumber: number;
  /** Bounding rect of the trigger, for fixed positioning (mirrors FindDriverPopover). */
  buttonRect: DOMRect | null;
  onClose: () => void;
  /** Called after a new date is added so the parent can refresh the pro view. */
  onAdded: () => void;
}

const POPOVER_WIDTH = 300;
const SPACING = 4;

/**
 * Storage Cleared popover for Pro Details. Lists the PRO's cms.storagecleared
 * history (newest first) and lets the user append a new "cleared through" date.
 * The table is append-only — no edits/deletes — so this only reads + creates.
 * Rendered via a portal with fixed positioning so it escapes the form's
 * overflow, the same approach as FindDriverPopover.
 */
const StorageClearedPopover: React.FC<StorageClearedPopoverProps> = ({
  isOpen,
  pronumber,
  buttonRect,
  onClose,
  onAdded,
}) => {
  const { showToast } = useToast();
  const [records, setRecords] = useState<StorageClearedRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [newDate, setNewDate] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  // Callback ref (as state) so the DateTimePicker's portalContainer is the
  // live popover element — its calendar then renders inside the popover's
  // stacking context (above the overlay) instead of behind it at body level.
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);

  const loadRecords = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      try {
        const res = await ordersService.getStorageCleared(pronumber, signal);
        setRecords(res.records);
      } catch (err: any) {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        console.error('Failed to load storage cleared history:', err);
        showToast({ type: 'error', message: 'Failed to load storage cleared history' });
      } finally {
        setLoading(false);
      }
    },
    [pronumber, showToast],
  );

  // Fetch the history each time the popover opens; reset the input.
  useEffect(() => {
    if (!isOpen) return;
    setNewDate(null);
    const controller = new AbortController();
    void loadRecords(controller.signal);
    return () => controller.abort();
  }, [isOpen, loadRecords]);

  const handleAdd = useCallback(async () => {
    if (!newDate || saving) return;
    setSaving(true);
    try {
      await ordersService.addStorageCleared(pronumber, toIsoDate(newDate));
      showToast({ type: 'success', message: 'Storage cleared date added' });
      setNewDate(null);
      await loadRecords(); // refresh the in-popover list
      onAdded(); // refresh the pro view (updates the inline indicator)
    } catch (err: any) {
      console.error('Failed to add storage cleared date:', err);
      const detail = err?.response?.data?.detail;
      showToast({ type: 'error', message: typeof detail === 'string' ? detail : 'Failed to add storage cleared date' });
    } finally {
      setSaving(false);
    }
  }, [newDate, saving, pronumber, showToast, loadRecords, onAdded]);

  if (!isOpen) return null;

  // Position: below the trigger, left-aligned; flip above if there isn't room.
  let top = 0;
  let left = 0;
  const estHeight = 320;
  if (buttonRect) {
    const spaceBelow = window.innerHeight - buttonRect.bottom;
    const openUpward = spaceBelow < estHeight && buttonRect.top > spaceBelow;
    top = openUpward ? Math.max(SPACING, buttonRect.top - estHeight - SPACING) : buttonRect.bottom + SPACING;
    // Left-align to the trigger, but keep the popover on-screen.
    left = Math.min(buttonRect.left, window.innerWidth - POPOVER_WIDTH - SPACING);
    if (left < SPACING) left = SPACING;
  }

  return createPortal(
    <>
      <div className="fixed inset-0 z-[99998]" onClick={onClose} />
      <div
        ref={setContainerEl}
        className="fixed z-[99999] bg-panel rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 flex flex-col"
        style={{ top: `${top}px`, left: `${left}px`, width: `${POPOVER_WIDTH}px`, maxHeight: `${estHeight}px` }}
      >
        <div className="px-3 py-2 bg-slate-700 dark:bg-slate-800 rounded-t-lg flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">Storage Cleared</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-300 hover:text-white"
            title="Close"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* History list */}
        <div className="overflow-y-auto flex-1 px-3 py-2">
          {loading ? (
            <div className="py-3 text-center text-xs text-muted">Loading…</div>
          ) : records.length === 0 ? (
            <div className="py-3 text-center text-xs text-muted">No storage cleared dates yet.</div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-muted text-left">
                  <th className="font-medium pb-1">Cleared Through</th>
                  <th className="font-medium pb-1">Entered By</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.recno} className="text-gray-800 dark:text-gray-200">
                    <td className="py-0.5 pr-2 whitespace-nowrap">{r.clearedDate || '—'}</td>
                    <td className="py-0.5">
                      {r.enteredBy || '—'}
                      {r.enteredAt && <span className="text-gray-400 dark:text-gray-500"> · {r.enteredAt}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* New date entry — app DateTimePicker so users get the number-to-date
            typing helpers. Its calendar portals into this popover (containerEl)
            so it layers above the overlay. */}
        <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">New:</label>
          <div className="flex-1 min-w-0">
            <DateTimePicker
              mode="date"
              selectionType="single"
              inputVariant="compact"
              labelType="none"
              autoApply
              usePortal
              portalContainer={containerEl}
              value={newDate}
              onChange={(val) => setNewDate(val instanceof Date ? val : null)}
              placeholder="MM/DD/YYYY"
            />
          </div>
          <button
            type="button"
            onClick={() => void handleAdd()}
            disabled={!newDate || saving}
            className="px-3 py-1 text-xs font-medium text-white bg-blue-600 border border-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default StorageClearedPopover;
